
import { Component, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { Entity } from '../../entity/entity';
import { OrgAddressDetailComponent } from '../../organization/org-address-detail.component';
import { OrgAddressGridComponent } from '../../organization/org-address-grid.component';
import { OrganizationAddress } from '../../organization/organization-address';
import { PersonLogin } from '../../person/person-login';
import { PartSearchResult } from "../../products/part-search-result";
import { OrganizationAddressService } from '../../services/organization-address-service';
import { PersonLoginService } from '../../services/person-login-service';
import { AddressUtils } from '../../utils/address-utils';
import { StringUtils } from '../../utils/string-utils';
import { AddressAlternativeDialogComponent } from '../address-alternative-dialog/address-alternative-dialog.component';

@Component({
  selector: 'address-selection-dialog',
  templateUrl: './dealer-address-selection-dialog.component.html',
  styleUrls: ['./dealer-address-selection-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [OrgAddressGridComponent, OrgAddressDetailComponent, MatProgressSpinner, MatDialogActions, FormsModule, MatButton]
})
export class DealerAddressSelectionDialogComponent {
  private readonly addressService = inject(OrganizationAddressService);
  private readonly dialogRef = inject<MatDialogRef<DealerAddressSelectionDialogComponent>>(MatDialogRef);
  private readonly dialog = inject(MatDialog);
  private readonly data = inject(MAT_DIALOG_DATA);


  searchResult: PartSearchResult;
  personLogin: PersonLogin;
  readonly errorMessage = signal<string>(undefined!);
  readonly isBusy = signal<boolean>(undefined!);
  readonly selectedAddress = signal<OrganizationAddress>(undefined!);
  readonly addressList = signal<OrganizationAddress[]>(undefined!);
  addAddress!: boolean;
  isNew: boolean = false;
  entity: Entity;

  constructor
    () {
    const data = this.data;

    this.entity = data.entity as Entity;
    this.searchResult = data.searchResult as PartSearchResult;
    this.personLogin = data.personLogin as PersonLogin;
    this.addressService.getOrganizationAddresses(this.personLogin.person.primaryOrgUnitId).subscribe({
      next: results => this.addressList.set(results.filter(address => address.typeCode === 'DEALER')),
      error: error => this.handleError(error)
    });
  }

  public clearSelectedAddress(): void {
    this.selectAddress(undefined!);
  }

  public selectAddress(address: OrganizationAddress): void {
    this.errorMessage.set(undefined!);
    this.selectedAddress.set(address);
    this.addAddress = false;
    this.isNew = false;
  }

  public newAddress(): void {
    this.errorMessage.set(undefined!);
    this.selectedAddress.set(new OrganizationAddress());
    this.selectedAddress().typeCode = "DEALER";
    this.selectedAddress().orgUnitId = this.entity?.repOfficeId || this.personLogin.person.primaryOrgUnitId;
    this.selectedAddress().createUser = this.personLogin.person.userLogin;
    this.selectedAddress().updateUser = this.personLogin.person.userLogin;
    this.addAddress = true;
    this.isNew = true;
  }

  public saveAndClose(): void {
    this.errorMessage.set(undefined!);
    this.isBusy.set(true);

    this.isBusy.set(true);
    if (!(this.selectedAddress().attentionLine && this.selectedAddress().attentionLine.length > 0)) {
      this.errorMessage.set("You must enter a Ship To Name");
      this.isBusy.set(false);
    } else if (!(this.selectedAddress().addressLine1 && this.selectedAddress().addressLine1.length > 0)) {
      this.errorMessage.set("You must enter an Address Line 1");
      this.isBusy.set(false);
    } else if (!(this.selectedAddress().cityName && this.selectedAddress().cityName.length > 0)) {
      this.errorMessage.set("You must enter a City Name");
      this.isBusy.set(false);
    } else if (!(this.selectedAddress().stateCode && this.selectedAddress().stateCode.length > 0)) {
      this.errorMessage.set("You must enter a State");
      this.isBusy.set(false);
    } else if (!(this.selectedAddress().postalCode && this.selectedAddress().postalCode.length > 0)) {
      this.errorMessage.set("You must enter a Postal Code");
      this.isBusy.set(false);
    } else {
      this.validateAddress();
    }
  }

  private validateAddress(): void {
    this.addressService.validateAddress(this.selectedAddress()).subscribe({
      next: resolution => {
        if (!resolution.validAddress || AddressUtils.validatedAddressIsDifferent(resolution.validatedAddresses, this.selectedAddress())) {
          const validatedAddress = resolution.validatedAddresses[0];
          const suggestionDialog = this.dialog.open(AddressAlternativeDialogComponent, {
            width: '1064px',
            height: '520px',
            data: { resolution: resolution, enteredAddress: this.selectedAddress(), validatedAddress: validatedAddress },
          });
          suggestionDialog.afterClosed().subscribe({
            next: result => {
              if (result) {
                this.selectedAddress.set(AddressUtils.updateAddressWithValidated(this.selectedAddress(), validatedAddress));
                this.selectedAddress().createUser = this.personLogin.person.userLogin;
                this.selectedAddress().updateUser = this.personLogin.person.userLogin;
                this.saveAndReturnAddress();
              } else {
                if (resolution.validAddress) {
                  this.saveAndReturnAddress();
                } else {
                  this.isBusy.set(false);
                }
              }
            },
            error: error => this.handleError(error)
          });
        } else {
          this.saveAndReturnAddress();
        }
      },
      error: error => this.handleError(error)
    });
  }

  private saveAndReturnAddress(): void {
    this.isBusy.set(false);
    this.selectedAddress().activeIndicator = this.isNew && this.addAddress;
    this.addressService.save(this.selectedAddress()).subscribe({
      next: result => {
        this.dialogRef.close(result);
      },
      error: error => this.handleError(error)
    });
  }

  public cancel(): void {
    this.dialogRef.close();
  }

  private handleError(error: any): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

}

